/**
 * Raffinato Wishlist Manager
 * localStorage-based wishlist with i18n support
 *
 * Strings are injected via a <script id="WishlistStrings"> JSON block
 * rendered in the Liquid layout so that translations come from locale files.
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'raffinato_wishlist';
    this.wishlist = this.load();
    this.strings = this.loadStrings();
    this.init();
  }

  /* ─── Locale strings ──────────────────────────── */

  loadStrings() {
    const el = document.getElementById('WishlistStrings');
    if (el) {
      try { return JSON.parse(el.textContent); } catch (e) { /* fall through */ }
    }
    // Fallback English defaults
    return {
      add_to_cart: 'Add to Cart',
      adding: 'Adding\u2026',
      added: 'Added!',
      added_to_wishlist: 'Added to wishlist',
      removed_from_wishlist: 'Removed from wishlist',
      empty_title: 'Your wishlist is empty',
      empty_text: 'Save your favorite pieces to revisit them anytime.',
      shop_now: 'Start Shopping',
      remove: 'Remove from wishlist',
      view_all: 'View All Saved Items',
      currency_symbol: '\u0631.\u0633',
      root_url: ''
    };
  }

  t(key) {
    return this.strings[key] || key;
  }

  /* ─── localStorage ────────────────────────────── */

  load() {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return [];
      const parsed = JSON.parse(data);
      // Validate structure
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(item => item && item.id && item.handle);
    } catch (e) {
      console.error('Wishlist: failed to load', e);
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('Wishlist: failed to save', e);
    }
  }

  /* ─── CRUD ────────────────────────────────────── */

  add(product) {
    if (this.has(product.id)) return false;
    this.wishlist.push({
      id: product.id,
      handle: product.handle,
      title: product.title,
      price: product.price,
      image: product.image,
      variant_id: product.variant_id,
      added_at: Date.now()
    });
    this.save();
    this.updateUI();
    return true;
  }

  remove(productId) {
    const idx = this.wishlist.findIndex(i => String(i.id) === String(productId));
    if (idx > -1) {
      this.wishlist.splice(idx, 1);
      this.save();
      this.updateUI();
      return true;
    }
    return false;
  }

  toggle(product) {
    if (this.has(product.id)) {
      this.remove(product.id);
      return false;
    }
    this.add(product);
    return true;
  }

  has(productId) {
    return this.wishlist.some(i => String(i.id) === String(productId));
  }

  getAll() { return this.wishlist; }
  count()  { return this.wishlist.length; }

  clear() {
    this.wishlist = [];
    this.save();
    this.updateUI();
  }

  /* ─── Helpers ─────────────────────────────────── */

  /** Escape HTML to prevent XSS from user-generated product titles */
  esc(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  /** Build a locale-aware product URL */
  productUrl(handle) {
    const root = this.t('root_url') || '';
    return root + '/products/' + handle;
  }

  /** Format price -- Shopify stores prices in cents */
  formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    return amount + ' ' + this.t('currency_symbol');
  }

  /* ─── UI updates ──────────────────────────────── */

  updateUI() {
    this.updateAllButtons();
    this.updateHeaderCount();
    this.renderWishlistPage();
    this.renderWishlistDrawer();
  }

  updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const active = this.has(btn.dataset.productId);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-label', active ? this.t('removed_from_wishlist') : this.t('added_to_wishlist'));
    });
  }

  updateHeaderCount() {
    const c = this.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = c;
      el.style.display = c > 0 ? 'flex' : 'none';
    });
  }

  /* ─── Wishlist page ───────────────────────────── */

  renderWishlistPage() {
    const container = document.getElementById('wishlistItems');
    if (!container) return;

    const emptyState  = document.getElementById('wishlistEmpty');
    const countDisplay = document.getElementById('wishlistCount');
    const isEmpty = this.count() === 0;

    container.style.display = isEmpty ? 'none' : 'grid';
    if (emptyState)  emptyState.style.display = isEmpty ? 'block' : 'none';
    if (countDisplay) countDisplay.textContent = this.count();

    if (isEmpty) return;

    container.innerHTML = this.wishlist.map(item => `
      <div class="wl-card" data-product-id="${this.esc(String(item.id))}">
        <div class="wl-card__media">
          <a href="${this.productUrl(item.handle)}">
            <img src="${this.esc(item.image)}" alt="${this.esc(item.title)}" loading="lazy" width="600" height="750">
          </a>
          <button class="wl-card__remove" data-remove-wishlist="${this.esc(String(item.id))}" aria-label="${this.t('remove')}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="wl-card__body">
          <h3 class="wl-card__title">
            <a href="${this.productUrl(item.handle)}">${this.esc(item.title)}</a>
          </h3>
          <p class="wl-card__price">${this.formatMoney(item.price)}</p>
          <button class="wl-card__atc btn btn--primary" data-add-to-cart="${this.esc(String(item.variant_id))}">
            ${this.t('add_to_cart')}
          </button>
        </div>
      </div>
    `).join('');
  }

  /* ─── Wishlist drawer ─────────────────────────── */

  renderWishlistDrawer() {
    const container = document.getElementById('wishlistDrawerItems');
    if (!container) return;

    const footer = document.getElementById('wishlistDrawerFooter');

    if (this.count() === 0) {
      container.innerHTML = `
        <div class="wishlist-drawer__empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <p>${this.t('empty_title')}</p>
          <a href="${(this.t('root_url') || '') + '/collections/all'}" class="btn btn--primary">${this.t('shop_now')}</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    container.innerHTML = this.wishlist.map(item => `
      <div class="wishlist-drawer-item" data-product-id="${this.esc(String(item.id))}">
        <div class="wishlist-drawer-item__image">
          <a href="${this.productUrl(item.handle)}">
            <img src="${this.esc(item.image)}" alt="${this.esc(item.title)}" loading="lazy" width="80" height="100">
          </a>
        </div>
        <div class="wishlist-drawer-item__details">
          <h3 class="wishlist-drawer-item__title">
            <a href="${this.productUrl(item.handle)}">${this.esc(item.title)}</a>
          </h3>
          <p class="wishlist-drawer-item__price">${this.formatMoney(item.price)}</p>
          <button class="wishlist-drawer-item__add-to-cart" data-add-to-cart="${this.esc(String(item.variant_id))}">
            ${this.t('add_to_cart')}
          </button>
        </div>
        <button class="wishlist-drawer-item__remove" data-remove-wishlist="${this.esc(String(item.id))}" aria-label="${this.t('remove')}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `).join('');

    if (footer) footer.style.display = 'block';
  }

  /* ─── Drawer open / close ─────────────────────── */

  openDrawer() {
    const drawer = document.getElementById('wishlistDrawer');
    if (!drawer) return;
    drawer.classList.add('wishlist-drawer--open');
    document.body.style.overflow = 'hidden';
    this.renderWishlistDrawer();
    // Trap focus
    const closeBtn = drawer.querySelector('.wishlist-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  closeDrawer() {
    const drawer = document.getElementById('wishlistDrawer');
    if (!drawer) return;
    drawer.classList.remove('wishlist-drawer--open');
    document.body.style.overflow = '';
    // Return focus to trigger
    const trigger = document.querySelector('[data-wishlist-trigger]');
    if (trigger) trigger.focus();
  }

  /* ─── Add to cart via AJAX ────────────────────── */

  async addToCart(variantId, quantity = 1) {
    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity })
      });
      if (!res.ok) return false;

      // Update cart count in header
      const cartRes = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
      if (cartRes.ok) {
        const cart = await cartRes.json();
        document.querySelectorAll('.header__cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      }

      // Open cart drawer if theme supports it
      if (window.theme && typeof window.theme.openCartDrawer === 'function') {
        window.theme.openCartDrawer();
      }
      return true;
    } catch (err) {
      console.error('Wishlist: add-to-cart failed', err);
      return false;
    }
  }

  /* ─── Event listeners (delegated) ─────────────── */

  init() {
    document.addEventListener('click', (e) => {
      // Wishlist toggle button
      const wishBtn = e.target.closest('[data-wishlist-btn]');
      if (wishBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleButtonClick(wishBtn);
        return;
      }

      // Remove button
      const removeBtn = e.target.closest('[data-remove-wishlist]');
      if (removeBtn) {
        e.preventDefault();
        this.remove(removeBtn.dataset.removeWishlist);
        return;
      }

      // Add-to-cart from wishlist
      const atcBtn = e.target.closest('[data-add-to-cart]');
      if (atcBtn) {
        e.preventDefault();
        const vid = atcBtn.dataset.addToCart;
        atcBtn.disabled = true;
        atcBtn.textContent = this.t('adding');
        this.addToCart(vid).then(ok => {
          atcBtn.disabled = false;
          atcBtn.textContent = ok ? this.t('added') : this.t('add_to_cart');
          if (ok) {
            setTimeout(() => { atcBtn.textContent = this.t('add_to_cart'); }, 2000);
          }
        });
        return;
      }

      // Open drawer
      if (e.target.closest('[data-wishlist-trigger]')) {
        e.preventDefault();
        this.openDrawer();
        return;
      }

      // Close drawer
      if (e.target.closest('.wishlist-drawer__close')) {
        e.preventDefault();
        this.closeDrawer();
        return;
      }

      // Overlay close
      if (e.target.closest('.wishlist-drawer__overlay')) {
        this.closeDrawer();
        return;
      }
    });

    // Escape key closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDrawer();
    });

    // Initial paint
    this.updateUI();
  }

  handleButtonClick(btn) {
    const product = {
      id: btn.dataset.productId,
      handle: btn.dataset.productHandle,
      title: btn.dataset.productTitle,
      price: parseInt(btn.dataset.productPrice, 10) || 0,
      image: btn.dataset.productImage,
      variant_id: btn.dataset.variantId
    };

    const added = this.toggle(product);
    btn.classList.toggle('is-active', added);
    this.showToast(added ? this.t('added_to_wishlist') : this.t('removed_from_wishlist'));
  }

  /* ─── Toast notification ──────────────────────── */

  showToast(message) {
    const existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
      <span>${this.esc(message)}</span>`;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }
}

// Initialise
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new WishlistManager();
});

// Re-paint on Shopify theme editor section load
document.addEventListener('shopify:section:load', () => {
  if (window.wishlist) window.wishlist.updateUI();
});
